import React, { Ref, useState, useRef, useEffect } from "react";
import { getCity, toast, domains, getTimeFormat } from "utils";
import { ArrowLeftIcon, MailIcon } from "@heroicons/react/solid";
import { useMutation } from "@tanstack/react-query";
import { api } from "utils/api";
import { useRouter } from "next/router";
import OtpBackButton from "components/OtpBackButton/OtpBackButton";
import {
  OfferAuthOtpPasteButton,
  OfferAuthOtpResendButton,
} from "components/OfferAuthOtpSupportActions/OfferAuthOtpSupportActions";
import OfferAuthEmailContinueButton from "components/OfferAuthEmailContinueButton/OfferAuthEmailContinueButton";
import OfferAuthOtpConfirmButton from "components/OfferAuthOtpConfirmButton/OfferAuthOtpConfirmButton";
import "i18n";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { navigateAfterAuth } from "utils/publicOfferNavigation";
import {
  filteredDomains,
  temporaryMails,
} from "../utils/filtered_domains_export";

interface FormData {
  email: string;
  language?: string;
}

const initialFormData: FormData = {
  email: "",
};

export const OfferSignInUpOtp = ({
  handleAuthForm,
  type = "appointment",
  emailRef = null,
  offerId,
  publicId,
  code,
  eventType,
}: {
  handleAuthForm(a): void;
  type?: "appointment" | "product" | "tip" | "community" | "event";
  emailRef?: Ref<HTMLInputElement>;
  offerId: string;
  publicId: string;
  code?: any;
  eventType?: string;
}) => {
  const router = useRouter();
  const [formData, setFormData] = useState(initialFormData);
  const [isTouched, setTouched] = useState(false);
  const [isSignUpLoading, setSignUpLoading] = useState<boolean>(false);
  const [isCodeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string[]>([
    "",
    "",
    "",
    "",
    "",
  ]);
  const [isValid, setIsValid] = useState(true);
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const inputRefs = useRef([]);
  const [timeLeft, setTimeLeft] = useState(null);
  const [hasEverBeenValid, setHasEverBeenValid] = useState(false);
  const [hasEmailError, setEmailError] = useState(true);
  const { t, i18n } = useTranslation();

  const sentCode = useMutation((payload: { email: string; otp: string }) =>
    api.post(`/users/v1/auth/o`, {
      ...payload,
      country: countryCode,
      timeZone: localTimeZone,
      city: getCity(localTimeZone),
      language: i18n.language,
    })
  );

  const handleSignUpCode = async () => {
    setSignUpLoading(true);

    try {
      await sentCode.mutateAsync(
        { ...formData, otp: verificationCode.join("") },
        {
          onSuccess() {
            Cookies.remove("sentEmail");
            Cookies.remove("path");
            Cookies.remove("Email");
            navigateAfterAuth(
              router,
              resolvedPath,
              code ? { promo: code } : {}
            ).then(() => {
              setSignUpLoading(false);
            });
          },
          onError(error) {
            setIsValid(false);
            setSignUpLoading(false);
          },
        }
      );
    } catch (e) {
      // Handle error
    }
  };

  useEffect(() => {
    if (verificationCode.every((el) => el === "") || !isCodeSent) {
      setIsValid(true);
    }
    if (
      verificationCode.length === 5 &&
      verificationCode.every((el) => el !== "")
    ) {
      handleSignUpCode();
    }
  }, [verificationCode, isCodeSent]);

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && e.target.value === "") {
      const previousInput = inputRefs.current[index - 1];
      if (previousInput) {
        previousInput.focus();
      }
    }
  };

  const handleCodeChange = async (e, index) => {
    const value = e.target.value.trim().replace(/\s/g, "");
    if (/^\d*$/.test(value)) {
      if (value.length <= 1) {
        setVerificationCode((prevCodes) => {
          const newCodes = [...prevCodes];
          newCodes[index] = value;
          return newCodes;
        });
        if (inputRefs.current[index + 1] && value.length === 1) {
          inputRefs.current[index + 1].focus();
        }
      }
    }
  };

  const handlePaste = async (e) => {
    e.preventDefault();
    try {
      const text = await navigator.clipboard.readText();
      if (/^\d{5}$/.test(text)) {
        setVerificationCode(text.split(""));
      }
    } catch (err) {
      console.log("Access to clipboard was denied");
    }
  };

  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timerId);
    }
  }, [timeLeft]);

  const handleResendCode = async () => {
    if (timeLeft > 0) {
      return;
    }
    setTimeLeft(30);
    setVerificationCode(["", "", "", "", ""]);
    await createAccount.mutateAsync(formData, {
      onError() {},
    });
  };

  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const createAccount = useMutation(
    (payload: { email: string; language?: string }) =>
      api.post(`/users/v1/auth/o-signin`, {
        ...payload,
        country: countryCode,
        timeZone: localTimeZone,
        city: getCity(localTimeZone),
        checkout: true,
        language: i18n.language,
        timeFormat: getTimeFormat(countryCode).replace("h", ""),
      })
  );

  const resolvedPath = `/${publicId}/${type}/${offerId}`;

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    setFormData({
      ...formData,
      [name]: value.trim().replace(/\s/g, ""),
    });

    const tlds = domains
      .flatMap((domain) => domain.tlds)
      .map((tld) => tld.replace(".", ""))
      .concat(filteredDomains.map((domain) => domain.replace(".", "")))
      .join("|");

    const emailRegex = new RegExp(
      `^[^\\s@]+@[^\\s@]+\\.(${tlds}|com|org|net|edu|gov|mil|co|co\\.uk|info|biz|io|me|ai|app|partners)$`,
      "i"
    );
    const domain = e.target.value.split("@")[1];
    const containsCyrillic = /[а-яёА-ЯЁ]/.test(value);
    const valid = emailRegex.test(e.target.value) && !containsCyrillic;

    setEmailError(!valid || temporaryMails.includes(domain));

    if (valid) {
      setHasEverBeenValid(true);
    }
  };

  const handleSubmitForm = async () => {
    const tlds = domains
      .flatMap((domain) => domain.tlds)
      .map((tld) => tld.replace(".", ""))
      .concat(filteredDomains.map((domain) => domain.replace(".", "")))
      .join("|");

    const emailRegex = new RegExp(
      `^[^\\s@]+@[^\\s@]+\\.(${tlds}|com|org|net|edu|gov|mil|co|co\\.uk|info|biz|io|me|ai|app|partners)$`,
      "i"
    );

    const containsCyrillic = /[а-яёА-ЯЁ]/.test(formData.email);

    const isValid =
      emailRegex.test(formData.email) &&
      !temporaryMails.includes(formData.email.split("@")[1]) &&
      !containsCyrillic;
    setIsValid(isValid);

    if (isValid) {
      setSignUpLoading(true);
      try {
        const response = await createAccount.mutateAsync(formData, {
          onError() {
            setEmailError(true);
            toast("An error occurred", { type: "error" });
          },
          onSuccess() {},
        });

        if (response !== true) {
          Cookies.remove("sentEmail");
          Cookies.remove("path");
          Cookies.remove("Email");
          toast(t("accountCreated"), { type: "success" });
          await navigateAfterAuth(
            router,
            resolvedPath,
            code ? { promo: code } : {}
          );
        } else {
          setCodeSent(true);
        }
      } catch (e) {
        return;
      } finally {
        setSignUpLoading(false);
      }
    } else {
      setIsValid(false);
    }
  };

  const isDisabled = isSignUpLoading || !formData.email || hasEmailError;

  const handleBack = () => {
    handleAuthForm(null);
  };

  const handleGoBack = () => {
    setCodeSent(false);
    setIsValid(true);
    setVerificationCode(["", "", "", "", ""]);
    setSignUpLoading(false);
    setTouched(false);
  };

  return (
    <>
      {!isCodeSent ? (
        <div className="text-center">
          <div
            onClick={handleBack}
            className="inline-flex items-center justify-center border border-slate-500 p-3 rounded-full cursor-pointer mb-6"
          >
            <ArrowLeftIcon className="h-4 w-4 text-slate-500" />
          </div>
          <h2 className="text-xl font-medium text-slate-500">
            {t("enterEmailToContinue")}
          </h2>

          <form
            className="flex flex-col w-full mt-6"
            onBlur={() => {
              setTouched(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmitForm();
              }
            }}
          >
            <div className="relative rounded-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MailIcon
                  className="h-5 w-5 text-slate-500"
                  aria-hidden="true"
                />
              </div>
              <input
                type="email"
                name="email"
                data-testid="email-input"
                className={`focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 py-3 sm:text-sm rounded-md text-slate-700 border-black/10 font-medium ${
                  hasEmailError && hasEverBeenValid
                    ? "text-red-500"
                    : "text-white"
                }`}
                placeholder={t("enterEmail")}
                onChange={handleChange}
                value={formData.email}
                ref={emailRef}
              />
            </div>

            {hasEmailError && hasEverBeenValid ? (
              <span className="text-red-500 text-start pt-1 text-sm">
                {t("invalidEmail")}!
              </span>
            ) : null}
            <div className="hidden">
              <CountrySelect selectCountryCode={setCountryCode} />
            </div>
            <div className="mt-6">
              <OfferAuthEmailContinueButton
                onClick={handleSubmitForm}
                disabled={isDisabled}
                loading={isSignUpLoading}
                label={t("continue")}
              />
            </div>
          </form>
        </div>
      ) : (
        <>
          <div className=" m-auto">
            <div className="text-center">
              <OtpBackButton onClick={handleGoBack} />

              <h2 className="text-3xl font-bold text-slate-500 mb-2">
                {t("enterCode")}
              </h2>

              <p className="text-sm mb-6">
                <span className="text-slate-500">{t("haveSentCodeTo")} </span>
                <span className="text-slate-700">{formData.email}.</span>
              </p>
            </div>

            <div className="flex gap-2 mb-1">
              {[...Array(5)].map((_, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  placeholder="-"
                  value={verificationCode[index]}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onChange={(e) => handleCodeChange(e, index)}
                  className={`w-11 h-11 rounded-md text-center bg-gray-50 placeholder-slate-200 font-bold p-4 shadow-md appearance-none border border-slate-200 outline-none ${
                    !isValid ? "text-red-500" : "text-slate-500"
                  }`}
                  ref={(ref) => (inputRefs.current[index] = ref)}
                  inputMode="numeric"
                  onPaste={handlePaste}
                />
              ))}
              {navigator.userAgent.indexOf("Firefox") != -1 ? null : (
                <OfferAuthOtpPasteButton
                  onClick={handlePaste}
                  label={t("paste")}
                />
              )}
            </div>
            {!isValid && (
              <p className="text-red-500 mb-4 text-sm">{t("incorrectCode")}</p>
            )}
            <div className="flex justify-between items-center text-sm font-bold">
              {timeLeft > 0 ? (
                <p className="text-slate-500">
                  {t("resendCodeIn", {
                    time: timeLeft,
                  })}
                </p>
              ) : (
                <OfferAuthOtpResendButton
                  onClick={handleResendCode}
                  label={t("resendCode")}
                />
              )}
            </div>
            <div className="mt-6 border-t border-slate-100">
              <OfferAuthOtpConfirmButton
                onClick={handleSignUpCode}
                disabled={
                  isSignUpLoading || verificationCode.some((el) => el === "")
                }
                loading={isSignUpLoading}
                label={t("confirm")}
                wideOnDesktop={eventType === "OfflineEvent"}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};
import { Fragment } from "react";
import { config } from "utils";
import { CheckIcon, GlobeIcon, SelectorIcon } from "@heroicons/react/solid";

import cx from "classnames";
import { useQuery } from "@tanstack/react-query";

import { Listbox, Transition } from "@headlessui/react";

function CountrySelect({ selectCountryCode }) {
  useQuery(["country"], () => api.get("/geolocation"), {
    onSuccess(data) {
      handleChange({
        label: data?.label,
        code: data?.country,
      });
    },
  });
  const countries = config.stripeSupportedCountries;

  const [selected, setSelected] = useState({
    label: null,
    code: null,
  });

  const handleChange = (country) => {
    selectCountryCode(country.code);
    setSelected(country);
  };
  const { t } = useTranslation();

  return (
    <Listbox value={selected} onChange={handleChange}>
      {({ open }) => (
        <>
          <div className="relative mt-1 w-full">
            <Listbox.Button className="relative w-full cursor-default rounded-md text-white border border-white/10 py-3 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
              <span className="flex items-center">
                <GlobeIcon className="h-5 w-5 text-gray-400" />
                {selected.label ? (
                  <span className="ml-3 block truncate font-semibold">
                    {selected.label}
                  </span>
                ) : (
                  <span className="ml-3 text-gray-500 font-semibold">
                    {t("chooseCountry")}...
                  </span>
                )}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                <SelectorIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>
            {/* <p className="text-white text-sm text-gray-500 font-semibold mt-2">
              {t("chooseCountryBank")}
            </p> */}

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-black text-white border border-white/10 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {countries.map((country) => (
                  <Listbox.Option
                    key={country.label}
                    className={({ active }) =>
                      cx(
                        active ? "text-white bg-indigo-600" : "text-gray-200",
                        "relative cursor-default select-none py-2 pl-3 pr-9"
                      )
                    }
                    value={country}
                  >
                    {({ selected, active }) => (
                      <>
                        <div className="flex items-center">
                          <span
                            className={cx(
                              selected ? "font-semibold" : "font-normal",
                              "ml-3 block truncate"
                            )}
                          >
                            {country.label}
                          </span>
                        </div>

                        {selected ? (
                          <span
                            className={cx(
                              active ? "text-white" : "text-indigo-600",
                              "absolute inset-y-0 right-0 flex items-center pr-4"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
}
